import clsx from "clsx";
import React, { useEffect } from "react";
import { DialogProps, TextField, TextFieldProps } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { BaseDialog } from "./BaseDialog";
import { FieldsGroup } from "../fields/FieldsGroup";
import { FieldError } from "../fields/FieldError";
import LoadingButton from "../button/LoadingButton";

const COMMON_SPACING = 7;

const useStyles = makeStyles((theme) => ({
  root: {},

  paper: {
    [theme.breakpoints.up("md")]: {
      minWidth: theme.spacing(84),
    },
  },

  control: {
    marginBottom: theme.spacing(4),
    width: "100%",
  },

  fieldGroupLabel: {
    marginBottom: theme.spacing(1),
  },

  error: {
    position: "absolute",
  },

  button: {
    height: theme.spacing(7),
    fontSize: 16,
    fontWeight: "bold",
    lineHeight: "20px",
    marginTop: theme.spacing(2),
  },
}));

export interface CreateProgramFromTemplateDialogProps extends DialogProps {
  templateName?: string;
  onConfirm: (name: string) => void;
  isPending?: boolean;
}

export function CreateProgramFromTemplateDialog(
  props: CreateProgramFromTemplateDialogProps,
) {
  const {
    className,
    onClose,
    templateName = "",
    onConfirm,
    isPending = false,
    ...other
  } = props;
  const s = useStyles();
  const [name, setName] = React.useState(templateName);
  const [error, setError] = React.useState("");

  const handleNameChange: TextFieldProps["onChange"] = React.useCallback(
    (event) => {
      const value = event.target.value;
      setName(value);

      if (error && value.trim().length >= 4) {
        setError("");
      }
    },
    [error],
  );

  useEffect(() => {
    setName(templateName);
  }, [templateName]);

  const resetState = React.useCallback(() => {
    setName(templateName);
    setError("");
  }, [templateName]);

  const handleClose = React.useCallback(() => {
    resetState();
    if (onClose) {
      onClose({}, "backdropClick");
    }
  }, [onClose, resetState]);

  const handleConfirm = React.useCallback(() => {
    if (name.trim().length < 4) {
      setError("Name must be at least 4 characters long");
      return;
    }

    if (!name.trim()) {
      setError("Name cannot be empty");
      return;
    }

    setError("");
    onConfirm(name.trim());
  }, [name, onConfirm]);

  const disabled = isPending;

  return (
    <BaseDialog
      className={clsx(s.root, className)}
      title="Create program from template"
      closeDisabled={disabled}
      onClose={handleClose}
      PaperProps={{ className: s.paper }}
      {...other}
    >
      <FieldsGroup
        className={clsx(s.control)}
        labelClassName={s.fieldGroupLabel}
        label="Program name"
        {...other}
      >
        <TextField
          variant="outlined"
          fullWidth
          id="name"
          placeholder="Enter program name..."
          value={name}
          onChange={handleNameChange}
          disabled={disabled}
          required
          autoFocus
        />
        {error && (
          <FieldError hideIcon={true} className={s.error}>
            {error}
          </FieldError>
        )}
      </FieldsGroup>

      <LoadingButton
        className={s.button}
        fullWidth
        variant="contained"
        onClick={handleConfirm}
        disabled={!name || disabled || !!error}
        loading={isPending}
      >
        Create program
      </LoadingButton>
    </BaseDialog>
  );
}
